import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { ErrorMessage, Field, FieldArray, FieldProps, Form, Formik } from 'formik';
import Button from '@material-ui/core/Button';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormControlProps,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    Typography,
} from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useHistory, useParams } from 'react-router';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import TextField from '@material-ui/core/TextField';
import clsx from 'clsx';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { initUser, UserDto } from 'api/user';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import { DictionaryDto, useContentApi, useDictionaryApi } from 'api/dictionary';
import { fetchRegionsDict } from 'api/regions';
import Spin from 'components/atoms/Spin/Spin';
import { CourierDetailsDto } from '../../api/couriers';
import { photoText, Roles } from '../../constants';
import ImageGallery from '../../components/molecules/ImageGallery/ImageGallery';
import { BranchDto, getOpenBranches } from '../../api/branches';
import FormikMaskedInput from '../../components/atoms/FormikMaskedInput/FormikMaskedInput';

const useStyles = makeStyles((theme) => ({
    root: {},
    mt5: {
        marginTop: theme.spacing(3),
        width: '100%',
    },
    checkList: {
        marginTop: theme.spacing(6),
    },
    progress: {
        height: '4px',
        width: '100%',
    },
    container: {
        position: 'relative',
    },
    title: {
        width: '100%',
        paddingLeft: theme.spacing(2),
    },
    login: {},
}));

interface Params {
    id: string | undefined;
}

const UserEdit = () => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [item, setItem] = useState<UserDto>(initUser());
    const [roles, setRoles] = useState<DictionaryDto[]>([]);
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [branches, setBranches] = useState<BranchDto[]>([]);
    const [id, setId] = useState<string | undefined>(undefined);
    const [photos, setPhotos] = useState<{ id: number; desc?: string }[]>([]);
    const params = useParams<Params>();
    const { post, put, getById } = useDictionaryApi<UserDto>('/users');
    const { getById: getCourierById } = useDictionaryApi<CourierDetailsDto>('/courier/detail');
    const { enqueueSnackbar } = useSnackbar();
    const { fetchAll: fetchRolesDict } = useContentApi<DictionaryDto>('/user-role');
    const [courierData, setCourierData] = useState<CourierDetailsDto | null>(null);

    const handleBack = () => {
        history.push('/cabinet/users');
    };

    const schema = () =>
        Yup.object().shape({
            id: Yup.number(),
            username: Yup.string()
                .when('roles', (roles: string[], schema: Yup.StringSchema<string>) => {
                    const isCourier = roles?.includes('COURIER');
                    const isStaffCourier = roles?.includes('STAFF_COURIER');
                    if (isCourier || isStaffCourier) {
                        schema.transform((value) => value.replace(/[^\d]/g, ''));
                    } else {
                        schema.matches(/^[a-z]+$/, 'Только буквы можно писать');
                    }
                })
                .matches(/^[a-z0-9._-]+$/i, 'Введите корректный логин')
                .required('Введите логин'),
            password: Yup.string().when(
                'id',
                (id: number | undefined, schema: Yup.StringSchema) => {
                    if (!id) {
                        return schema.required('Введите пароль');
                    }
                }
            ),
            passwordConfirm: Yup.string()
                .when('id', (id: number | undefined, schema: Yup.StringSchema) => {
                    if (!id) {
                        return schema.required('Повторите пароль');
                    }
                })
                .oneOf([Yup.ref('password'), null], 'Введенный пароль не совпадает'),
            first_name: Yup.string().required('Введите имя'),
            last_name: Yup.string().required('Введите фамилия'),
            idn: Yup.string()
                .transform((value) => value.replace(/[^\d]/g, ''))
                .min(12, 'Минимально 12 символов')
                .max(12, 'Максимально 12 символов')
                .notRequired(),
            region: Yup.object()
                .typeError('Выберите регион')
                .shape({
                    id: Yup.number().required('Выберите регион'),
                }),
            branch: Yup.object()
                .typeError('Выберите регион')
                .shape({
                    id: Yup.number().required('Выберите филиал'),
                }),
            email: Yup.string().email('Введите корректную почту').notRequired(),
            roles: Yup.array().min(1, 'Выберите роль'),
            pus_username: Yup.string().matches(
                /^[a-z0-9-._]+$/,
                'Только буквы в нижнем регистре можно писать'
            ),
            personnelNumber: Yup.string().when(
                'roles',
                (roles: string[], schema: Yup.StringSchema<string>) => {
                    const isCashier = roles?.includes('CASHIER');

                    if (isCashier) {
                        return schema.required('Введите табельный номер');
                    }
                }
            ),
            colvirId: Yup.string().when(
                'roles',
                (roles: string[], schema: Yup.StringSchema<string>) => {
                    const isCashier = roles?.includes('CASHIER');

                    if (isCashier) {
                        return schema.required('Введите ID в Colvir-e');
                    }
                }
            ),
        });

    const loadBranches = useCallback(
        async (regionId: number | string) => {
            const regionCode = regions.find((r) => r.id === regionId)?.code;
            if (!regionCode) {
                return;
            }

            try {
                const branchList = await getOpenBranches({
                    value: null,
                    region_code: regionCode as string,
                });
                setBranches(branchList);
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar, regions]
    );

    const handleRegionChange = useCallback(
        async (regionId: string | number) => {
            await loadBranches(regionId);
        },
        [loadBranches]
    );

    const load = useCallback(async () => {
        setId(params.id);
        setLoading(true);
        try {
            const regionList = await fetchRegionsDict();
            setRegions(regionList);
            const roleList = await fetchRolesDict();
            setRoles(roleList.filter((r) => r.code !== 'ANONYMOUS'));
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            if (!params.id) {
                setLoading(false);
            }
        }
    }, [enqueueSnackbar, fetchRolesDict, params.id]);

    useEffect(() => {
        load();
    }, [load]);

    const loadUser = useCallback(async () => {
        if (params.id !== undefined) {
            try {
                const user = await getById(params.id);
                if (user.roles.includes(Roles.Courier) || user.roles.includes(Roles.Cashier)) {
                    try {
                        const courier = await getCourierById(params.id);
                        setItem({
                            ...initUser(user),
                            pus_username: courier?.courier?.pus_username,
                            personnelNumber: courier?.courier?.personnel_number,
                            colvirId: courier?.courier?.colvir_id,
                        });
                        setCourierData(courier);
                        const photoList = courier.courier_assets_group.photos.map((p: any) => ({
                            id: p.asset_id,
                            desc: photoText.get(p.type),
                        }));
                        setPhotos(photoList);
                    } catch (e) {
                        enqueueSnackbar('Данные курьера отсутствует', { variant: 'warning' });
                    } finally {
                        setLoading(false);
                    }
                } else {
                    setItem(initUser(user));
                    setLoading(false);
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, [enqueueSnackbar, getById, getCourierById, params.id]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    useEffect(() => {
        if (item.region?.id) {
            loadBranches(item.region.id);
        }
    }, [item.region, loadBranches]);

    useEffect(() => {
        setItem({ ...item, pus_username: courierData?.courier?.pus_username });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courierData]);

    return (
        <Box position="relative">
            <Formik
                enableReinitialize={true}
                initialValues={item}
                validationSchema={schema()}
                onSubmit={(values, { setSubmitting }) => {
                    if (item === undefined) {
                        return;
                    }

                    const isCourier = values.roles.includes('COURIER');
                    const isStaffCourier = values.roles.includes('STAFF_COURIER');
                    if (isCourier || isStaffCourier) {
                        const isCommonCourier = isCourier && isStaffCourier;
                        if (!isCommonCourier) {
                            enqueueSnackbar(
                                'Еслы вы добавляете Курьера или Штатного курьера, нужно выбрать роли Курьер и Штатный курьер',
                                { variant: 'error' }
                            );
                            setSubmitting(false);
                            return;
                        }
                    }

                    setSubmitting(true);
                    if (id === undefined) {
                        post({
                            ...values,
                            ...{ branch: values.branch?.id ? values.branch : null },
                            ...{ username: values.username.toLowerCase() },
                            ...{ pus_username: values.pus_username?.toLowerCase() },
                        })
                            .then(() => {
                                setSubmitting(false);
                                handleBack();
                            })
                            .catch((e) => {
                                enqueueSnackbar(
                                    `Ошибка при сохранении данных: ${
                                        e.response?.data?.message || e
                                    }`,
                                    {
                                        variant: 'error',
                                    }
                                );
                            })
                            .finally(() => setSubmitting(false));
                    } else {
                        const sendData = {
                            ...item,
                            ...values,
                            ...{ branch: values.branch?.id ? values.branch : null },
                            ...{ username: values.username.toLowerCase() },
                            ...{ pus_username: values.pus_username?.toLowerCase() },
                        };
                        put(sendData)
                            .then(() => {
                                handleBack();
                            })
                            .catch((e) => {
                                enqueueSnackbar(
                                    `Ошибка при сохранении данных: ${
                                        e.response?.data?.message || e
                                    }`,
                                    {
                                        variant: 'error',
                                    }
                                );
                            })
                            .finally(() => setSubmitting(false));
                    }
                }}
            >
                {({ isSubmitting, values, submitForm }) => (
                    <Form className={classes.root}>
                        <Grid
                            className={classes.container}
                            container
                            item
                            spacing={4}
                            xs={12}
                            md={10}
                            xl={8}
                            lg={8}
                        >
                            {loading && <Spin />}
                            <Typography variant="h3" className={classes.title}>
                                {id ? 'Редактирование' : 'Добавление'} пользователя
                            </Typography>
                            <Grid item xs={6}>
                                <Field
                                    component={
                                        values.roles.includes('COURIER') ||
                                        values.roles.includes('STAFF_COURIER')
                                            ? FormikMaskedInput
                                            : FormikTextField
                                    }
                                    className={clsx(classes.mt5, classes.login)}
                                    name="username"
                                    label="Логин"
                                    mask={
                                        values.roles.includes('COURIER') ||
                                        values.roles.includes('STAFF_COURIER')
                                            ? '79999999999'
                                            : undefined
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password',
                                        form: {
                                            autoComplete: 'off',
                                        },
                                    }}
                                />

                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    inputProps={{
                                        autoComplete: 'new-password',
                                        form: {
                                            autoComplete: 'off',
                                        },
                                    }}
                                    name="password"
                                    type="password"
                                    label="Пароль"
                                />
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="passwordConfirm"
                                    type="password"
                                    inputProps={{
                                        autoComplete: 'new-password',
                                        form: {
                                            autoComplete: 'off',
                                        },
                                    }}
                                    label="Повторите пароль"
                                />
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="last_name"
                                    label="Фамилия"
                                />
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="first_name"
                                    label="Имя"
                                />
                                <Field
                                    name="personnelNumber"
                                    component={FormikTextField}
                                    label="Табельный номер (не обязательное)"
                                    className={classes.mt5}
                                />
                                <FormControl className={classes.checkList}>
                                    <FormLabel>Роли</FormLabel>
                                    <FormGroup>
                                        <FieldArray
                                            name="roles"
                                            render={({ push, remove }) => (
                                                <div>
                                                    {roles.map((role) => (
                                                        <div key={role.id}>
                                                            <Field
                                                                component={() => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                value={role.code}
                                                                                color="primary"
                                                                                checked={values.roles.includes(
                                                                                    role.code
                                                                                )}
                                                                                onChange={(
                                                                                    e: ChangeEvent<
                                                                                        HTMLInputElement
                                                                                    >
                                                                                ) => {
                                                                                    if (
                                                                                        e.target
                                                                                            .checked
                                                                                    ) {
                                                                                        push(
                                                                                            role.code
                                                                                        );
                                                                                    } else {
                                                                                        const idx = values.roles.indexOf(
                                                                                            role.code
                                                                                        );
                                                                                        remove(idx);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={role.description}
                                                                    />
                                                                )}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                        <ErrorMessage name="roles">
                                            {(msg) => <FormHelperText error>{msg}</FormHelperText>}
                                        </ErrorMessage>
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="middle_name"
                                    label="Отчество"
                                />
                                <Field name="region">
                                    {(
                                        props: FieldProps &
                                            React.PropsWithChildren<any> &
                                            FormControlProps
                                    ) => {
                                        const { meta, form, field } = props;
                                        const showError = meta.touched && meta.error;
                                        return (
                                            <FormControl className={classes.mt5}>
                                                <Autocomplete
                                                    id="region"
                                                    options={regions}
                                                    value={field.value}
                                                    getOptionLabel={(option) => {
                                                        return option.description;
                                                    }}
                                                    getOptionSelected={(option, value) => {
                                                        return option.id === value.id;
                                                    }}
                                                    onChange={(_e: any, value: any) => {
                                                        form.setFieldValue('region', value);
                                                        form.setFieldValue('branch', null);
                                                        handleRegionChange(value?.id);
                                                    }}
                                                    renderInput={(params) => {
                                                        return (
                                                            <TextField
                                                                label="Регион"
                                                                name="region"
                                                                error={!!showError}
                                                                {...params}
                                                            />
                                                        );
                                                    }}
                                                />

                                                {!!showError && meta.error && meta.error?.id && (
                                                    <FormHelperText error={!!showError}>
                                                        {meta?.error?.id}
                                                    </FormHelperText>
                                                )}
                                                {!!showError && typeof meta.error === 'string' && (
                                                    <FormHelperText error={!!showError}>
                                                        {meta.error}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        );
                                    }}
                                </Field>

                                <Field name="branch">
                                    {({ ...props }) => {
                                        const { form, field, meta } = props;
                                        const showError = meta.touched && meta.error;
                                        return (
                                            <>
                                                <FormControl className={classes.mt5}>
                                                    <Autocomplete
                                                        id="branch"
                                                        options={branches}
                                                        value={field.value}
                                                        onChange={(_e: any, value: any) => {
                                                            form.setFieldValue('branch', value);
                                                        }}
                                                        getOptionLabel={(option) => {
                                                            return option?.code
                                                                ? `${option.code} ${option.title}`
                                                                : '';
                                                        }}
                                                        getOptionSelected={(option, value) => {
                                                            return option.id === value.id;
                                                        }}
                                                        renderInput={(params) => {
                                                            return (
                                                                <TextField
                                                                    label="Филиал"
                                                                    name="branch"
                                                                    error={!!showError}
                                                                    {...params}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                    {!!showError &&
                                                        meta.error &&
                                                        meta.error?.id && (
                                                            <FormHelperText error={!!showError}>
                                                                {meta?.error?.id}
                                                            </FormHelperText>
                                                        )}
                                                    {!!showError &&
                                                        typeof meta.error === 'string' && (
                                                            <FormHelperText error={!!showError}>
                                                                {meta.error}
                                                            </FormHelperText>
                                                        )}
                                                </FormControl>
                                            </>
                                        );
                                    }}
                                </Field>
                                <Field
                                    component={FormikTextField}
                                    className={clsx(classes.mt5, classes.login)}
                                    name="pus_username"
                                    label="Логин в пус системе"
                                />
                                <Field
                                    component={FormikTextField}
                                    className={clsx(classes.mt5, classes.login)}
                                    name="colvirId"
                                    label="Colvir ID"
                                />
                            </Grid>
                            <div className={classes.progress}>
                                {isSubmitting && <LinearProgress />}
                            </div>
                            <Grid item container xs={12} justify="center" spacing={2}>
                                <Button
                                    disabled={isSubmitting}
                                    onClick={submitForm}
                                    variant="contained"
                                    color="primary"
                                >
                                    Сохранить
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleBack}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Отмена
                                </Button>
                            </Grid>
                        </Grid>
                        {!loading && photos != null && photos.length > 0 && (
                            <Box marginTop={2}>
                                <Typography variant="h4">Фотографии</Typography>
                                <Box marginTop={3} margin={3}>
                                    <ImageGallery photos={photos} />
                                </Box>
                            </Box>
                        )}
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UserEdit;
